import { createContext, useContext, useState, useCallback, useEffect } from 'react'

const DocumentCreationContext = createContext(null)

export function DocumentCreationProvider({ children }) {
    // Document State - for the document generation pipeline
    const [document, setDocument] = useState({
        name: '',
        templateType: {
            template_id: '',
            template_name: '',
            source_document_name: '',
            url: '',
            detected_document_type: '',
            sections: [],
        },
        selectedSections: [],
        referenceFiles: [],
        queries_with_variables: [],
    })

    const [allSections, setAllSections] = useState([])

    // Template Details State - for template creation pipeline
    const [templateDetails, setTemplateDetails] = useState({
        templateName: '',
        source_document_name: '',
        url: '',
        detected_document_type: '',
        template_id: ''
    })

    // Reference File State
    const [referenceFile, setReferenceFile] = useState(null)

    // Update Document
    const updateDocument = useCallback((documentData) => {
        setDocument(prev => ({
            ...prev,
            ...documentData
        }))
    }, [])

    // Update Document Details
    const updateDocumentDetails = useCallback((details) => {
        setTemplateDetails(prev => ({
            ...prev,
            ...details
        }))
    }, [])

    // Update Reference File
    const updateReferenceFile = useCallback((file) => {
        setReferenceFile(file)
    }, [])

    // Reset all data
    const resetDocumentCreation = useCallback(() => {
        setDocument({
            name: '',
            templateType: null,
        })
        setTemplateDetails({
            name: '',
            description: '',
            type: '',
            styleGuide: '',
        })
        setReferenceFile(null)
        setAllSections([])
    }, [])

    // Get summary of all changes
    const getDocumentSummary = useCallback(() => {
        return {
            name: document,
            template: templateDetails,
            referenceFile,
            hasChanges: document.name || templateDetails.name || referenceFile
        }
    }, [document, templateDetails, referenceFile])

    const getDocSummary = useCallback(() => {
        return {
            name: templateDetails.name,
            description: templateDetails.description,
            type: templateDetails.type,
            styleGuide: templateDetails.styleGuide,
        }
    }, [templateDetails])

    const value = {
        // Data Structures
        documentSummary: getDocSummary,

        // Document Generation State
        document,
        updateDocument,

        // Template Creation State
        templateDetails,
        allSections,
        referenceFile,

        // Actions
        updateDocumentDetails,
        updateReferenceFile,
        resetDocumentCreation,
        getDocumentSummary,
        setAllSections
    }

    return (
        <DocumentCreationContext.Provider value={value}>
            {children}
        </DocumentCreationContext.Provider>
    )
}

export function useDocumentCreation() {
    const context = useContext(DocumentCreationContext)
    if (!context) {
        throw new Error('useDocumentCreation must be used within a DocumentCreationProvider')
    }
    return context
} 